import { useEffect, useMemo, useRef, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { anchoredMenuPosition } from '../../utils/anchoredMenuPosition';
import { lockPageScroll, unlockPageScroll } from '../../utils/pageScrollLock';
import { CheckIcon, type IconProps } from './icons';
import './popoverMenu.css';

export interface PopoverMenuItem<TKey extends string = string> {
  key: TKey;
  label: string;
  icon: ComponentType<IconProps>;
  destructive?: boolean;
  /** Marks the active option — renders a check on the trailing edge. */
  selected?: boolean;
}

interface PopoverMenuProps<TKey extends string = string> {
  /** Rect of the trigger button; the menu is null until one is supplied. */
  anchorRect: DOMRect | null;
  items: readonly PopoverMenuItem<TKey>[];
  /** Names the menu for assistive technology. */
  label: string;
  labelledBy?: string;
  density?: 'default' | 'compact';
  /** Keeps lightweight action menus anchored without shifting the feed body. */
  lockScroll?: boolean;
  verticalPlacement?: 'auto' | 'above' | 'below';
  horizontalPlacement?: 'left' | 'right';
  onClose: () => void;
  onSelect: (key: TKey) => void;
}

const MAX_WIDTH = 286;
const COMPACT_MAX_WIDTH = 216;
const MAX_HEIGHT = 380;
const EDGE_GAP = 12;
const ANCHOR_GAP = 10;
/** `.popover-menu__item` min-height plus the 2px it is offset from its sibling. */
const ITEM_HEIGHT = 50;
const COMPACT_ITEM_HEIGHT = 44;
/** `.popover-menu__list` vertical padding. */
const LIST_PADDING = 16;
const COMPACT_LIST_PADDING = 8;
const ITEM_SELECTOR = '.popover-menu__item';

export function PopoverMenu<TKey extends string = string>({
  anchorRect,
  items,
  label,
  labelledBy = 'popover-menu-title',
  density = 'default',
  lockScroll = true,
  verticalPlacement = 'auto',
  horizontalPlacement = 'left',
  onClose,
  onSelect,
}: PopoverMenuProps<TKey>) {
  const onCloseRef = useRef(onClose);
  const menuRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  onCloseRef.current = onClose;

  // Most callers rebuild `items` every render, so the count is what placement
  // actually depends on.
  const itemCount = items.length;
  const style = useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return undefined;
    const compact = density === 'compact';
    const width = Math.min(
      compact ? COMPACT_MAX_WIDTH : MAX_WIDTH,
      window.innerWidth - EDGE_GAP * 2,
    );
    const maxHeight = Math.min(MAX_HEIGHT, window.innerHeight - EDGE_GAP * 2);
    const itemHeight = compact ? COMPACT_ITEM_HEIGHT : ITEM_HEIGHT;
    const listPadding = compact ? COMPACT_LIST_PADDING : LIST_PADDING;
    return {
      // Measured from the item count rather than the cap, so a short menu opens
      // right under its trigger instead of being pushed up as if it were tall.
      ...anchoredMenuPosition(anchorRect, {
        width,
        height: Math.min(maxHeight, itemCount * itemHeight + listPadding),
        gap: ANCHOR_GAP,
        edge: EDGE_GAP,
        verticalPlacement,
        horizontalPlacement,
      }),
      width,
      maxHeight,
    };
  }, [
    anchorRect,
    density,
    horizontalPlacement,
    itemCount,
    verticalPlacement,
  ]);

  const isOpen = Boolean(anchorRect);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Pin the body so the page behind the menu cannot scroll away from it. The
    // lock also keeps the pinned offset readable, so an action taken from the
    // menu (edit a post) can record where the list was.
    if (lockScroll) lockPageScroll();

    // An aria-modal dialog that leaves focus outside itself traps keyboard users
    // on the trigger behind it, so focus moves in and is handed back on close.
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const itemButtons = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>(ITEM_SELECTOR) ?? []);
    // preventScroll: the menu is fixed at the trigger, and the body is pinned,
    // so there is nothing to scroll into view.
    itemButtons()[0]?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Stopped here so an enclosing dialog does not close along with the
        // menu; capture phase puts this ahead of listeners on the document.
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = itemButtons();
      if (focusable.length === 0) return;
      const edgeItem = event.shiftKey ? focusable[0] : focusable[focusable.length - 1];
      const active = document.activeElement;
      if (active === edgeItem || !menuRef.current?.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (lockScroll) unlockPageScroll();
      const returnTo = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, [isOpen, lockScroll]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="popover-menu__backdrop"
      data-focus-trap-overlay="true"
      role="presentation"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <section
        ref={menuRef}
        className={`popover-menu${density === 'compact' ? ' popover-menu--compact' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        style={style}
      >
        <div className="sr-only" id={labelledBy}>{label}</div>
        <div className="popover-menu__list" aria-label={label}>
          {items.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                className={`popover-menu__item${item.destructive ? ' popover-menu__item--destructive' : ''}`}
                onClick={() => onSelect(item.key)}
                // Only the chosen item carrying aria-pressed would leave a
                // screen reader unable to tell the other options are its
                // alternatives, so every item in a selectable menu reports it.
                aria-pressed={item.selected === undefined ? undefined : item.selected}
              >
                <span className="popover-menu__icon" aria-hidden="true"><Icon size={20} /></span>
                <span className="popover-menu__label">{item.label}</span>
                {item.selected && (
                  <span className="popover-menu__check" aria-hidden="true">
                    <CheckIcon size={18} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    </div>,
    document.body,
  );
}
